import migrations from '@app/icare/db/migrations'
import { AppMigrator } from '@pkg/db/AppMigrator'
import { AppMigratorProvider } from '@pkg/db/AppMigratorProvider'
import { isAppError } from '@pkg/shared/errors'
import { pollUntilReady } from '@pkg/shared/utils/pollUntilReady'
import { createDatabaseInitializer } from '@pkg/sync/client'
import { environmentManager } from '@tanstack/react-query'
import {
  DummyDriver,
  Kysely,
  ParseJSONResultsPlugin,
  SqliteAdapter,
  SqliteIntrospector,
  SqliteQueryCompiler,
} from 'kysely'
import { SerializePlugin } from 'kysely-plugin-serialize'
import { SQLocalKysely } from 'sqlocal/kysely'
import { apiClient } from './api'
import type { ClientDatabase } from './db/schema'

// Initialize SQLocalKysely and pass the dialect to Kysely
export const sqlocal = !environmentManager.isServer()
  ? new SQLocalKysely({
      databasePath: 'icare.sqlite3',
      // verbose: true,
    })
  : undefined

export const db = !environmentManager.isServer()
  ? new Kysely<ClientDatabase>({
      // biome-ignore lint/style/noNonNullAssertion:  On server only. same check
      dialect: sqlocal!.dialect!,
      plugins: [new SerializePlugin({}), new ParseJSONResultsPlugin()],
    })
  : new Kysely<ClientDatabase>({
      dialect: {
        createAdapter() {
          return new SqliteAdapter()
        },
        createDriver() {
          return new DummyDriver()
        },
        createIntrospector(db: Kysely<unknown>) {
          return new SqliteIntrospector(db)
        },
        createQueryCompiler() {
          return new SqliteQueryCompiler()
        },
      },
    })

type SnapshotPollResult =
  | { ready: true; downloadUrl: string; uncompressedSize?: number; retryAfterMs?: number }
  | { ready: false; retryAfterMs: number }

const pollSnapshotOnce = async (): Promise<SnapshotPollResult> => {
  try {
    const r = await apiClient.snapshot.icareDb({})
    return { ready: true, downloadUrl: r.downloadUrl, uncompressedSize: r.uncompressedSize }
  } catch (err) {
    if (isAppError(err) && err.code === 'SERVICE_UNAVAILABLE') {
      const retryAfterMs = (err.data as { retryAfterMs?: number } | undefined)?.retryAfterMs ?? 10_000
      return { ready: false, retryAfterMs }
    }
    throw err
  }
}

export const initializer = createDatabaseInitializer({
  sqlocal,
  fetchSnapshot: async () => {
    const ready = await pollUntilReady(
      pollSnapshotOnce,
      (r): r is { ready: true; downloadUrl: string; uncompressedSize?: number } => r.ready,
    )
    return { url: ready.downloadUrl, uncompressedSize: ready.uncompressedSize }
  },
  onInit: async () => {
    const migrator = new AppMigrator({
      db,
      provider: new AppMigratorProvider({
        migrations: migrations,
        scope: 'client',
      }),
    })
    await migrator.migrateToLatest()
  },
})
